// tslint:disable:naming-convention
/**
 * This class is the required return type for methods that wish to participate in a pipeline.
 */
export class PipelineResult<T> {
    /**
     * Creates a new PipelineResult. Should not be invoked directly.
     *
     * @private
     * @constructor
     * param {Boolean} finished - States whether the last computation should end the pipeline.
     * param {T} value - The current state used in the pipeline.
     */
    constructor(public Finished: boolean, public Value: T) { }

    /**
     * Creates a finished PipelineResult
     *
     * param {T} value - The current state used in the pipeline.
     * return {PipelineResult<T>} - A finished PipelineResult
     */
    public static Finished<T>(value: T) { return new PipelineResult(true, value); }

    /**
     * Creates a unfinished PipelineResult
     * param {T} value - The current state used in the pipeline.
     * return {PipelineResult<T>} - An unfinished PipelineResult
     */
    public static Unfinished<T>(value: T) { return new PipelineResult(false, value); }
}

/**
 * This is the type of all methods that wish to participate in a pipeline.
 */
export interface PipelineFunction<T> extends Function {
    (input: T): PipelineResult<T>;
}

/**
 * This is a helper class that facilitates railway oriented programming techniques.
 * Multiple functions can participate in a pipeline.
 * Their return values determine whether or not the remaining functions are invoked.
 */
export class Pipeline<T> {
    private readonly Pipeline: Array<PipelineFunction<T>>;

    /**
     * Creates a new Pipeline
     *
     * @constructor
     * @param {PipelineFunction<T>[]} pipeline - This is the list of functions that will participate in the pipeline
    */
    constructor(...pipeline: Array<PipelineFunction<T>>) {
        this.Pipeline = pipeline;
    }

    /**
     * This method coordinates the pipeline by running the methods as appropriate.
     *
     * @param {T} value - This is the initial value provided to the pipeline.
     * @return {T} - This is the final result of the pipeline.
     */
    public Run(value: T) {
        if (!this.Pipeline || this.Pipeline.length === 0) {
            return value;
        }

        let result: PipelineResult<T> = null;

        for (let i = 0; i < this.Pipeline.length; i++) {
            result = this.Pipeline[i](value);

            if (result.Finished) {
                return result.Value;
            }

            value = result.Value;
        }

        return !!result ? result.Value : value;
    }
}
